import { useState } from "react";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { PromotionFilterType, PromotionType } from "../../interfaces/promotionType";
import dayjs from "dayjs";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  promotionSchema,
  usePromotions,
  useSavePromotion,
  usePromotionStatusToggle,
  usePromotionByIdQuery,
  PromotionForm,
  PromotionTable,
  PromotionHeader,
} from "../../features/promotions";

const filterInit: PromotionFilterType = {};

const formDataInit: PromotionType = {
  promotionName: "",
  distributorTypeIds: [],
  discountTarget: "all",
  discountPercentage: null,
  promotionStartAt: dayjs().toDate(),
  promotionEndsAt: null,
  active: true,
  applyBy: "product-category",
};

export const PromotionsPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingPromotionId, setEditingPromotionId] = useState<string | null>(
    null,
  );
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "createdAt",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<PromotionFilterType>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, promotionName: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { promotions, paginationData, isLoading } = usePromotions({
    filter,
    pagination,
  });

  const { data: promotionToEdit } = usePromotionByIdQuery(
    editingPromotionId ?? "",
  );

  const { handleSave } = useSavePromotion();
  const { handleEnable, handleDisable } = usePromotionStatusToggle();

  const formik = useFormik<PromotionType>({
    initialValues: promotionToEdit?.data || formDataInit,
    validationSchema: promotionSchema,
    enableReinitialize: true,
    onSubmit: (
      values: PromotionType,
      formikHelpers: FormikHelpers<PromotionType>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewPromotionModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingPromotionId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingPromotionId(null);
    setShowModal(false);
  };

  const handleEditPromotion = (data: PromotionType) => {
    const { _id: promotionId } = data;
    if (!promotionId) return;
    setEditingPromotionId(promotionId);
    setShowModal(true);
  };

  return (
    <div>
      <PromotionHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNewPromotionModal} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <PromotionTable
        data={promotions}
        isLoading={isLoading}
        sortBy={pagination.sortBy as keyof PromotionType}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditPromotion,
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <PromotionForm formik={formik} />
      </Modal>
    </div>
  );
};
